import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { followPlayer, unfollowPlayer } from '../../../features/scout/scoutProfileSlice';
import { normalizePlayerClass } from '../../../utils/normalizePlayerClass';
import { openOnRowClick } from '../../../utils/rowClick';

export const StatisticsPlayerRow = ({ player, dynamicColumns = [], getStatValue: getStatValueFromParent = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleViewProfile = () => {
    // Use player ID to navigate
    const playerId = player._id || player.id || player.name.toLowerCase().replace(/\s+/g, '-');
    navigate(`/scout/player/${playerId}`, {
      state: {
        from: 'statistics',
        playerData: {
          ...player,
          // Ensure team data is included
          team: player.team
        }
      }
    });
  };

  const handleFollowToggle = (e) => {
    e.stopPropagation();
    const playerId = player._id;
    if (player.isFollowing) {
      dispatch(unfollowPlayer(playerId));
    } else {
      dispatch(followPlayer(playerId));
    }
  };

  // Extract data from API response structure
  const jpRank = player.jpRank || player.rank ||
    (Array.isArray(player.battingStats) ? player.battingStats[0]?.jpRank : player.battingStats?.jpRank) ||
    (Array.isArray(player.pitchingStats) ? player.pitchingStats[0]?.jpRank : player.pitchingStats?.jpRank) ||
    'NA';
  const toTitleCase = (str) => str ? str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : str;
  const playerName = toTitleCase(player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim()) || 'N/A';
  const position = player.primaryPosition || player.position || '-';
  const playerClass = normalizePlayerClass(player.playerClass || player.class);
  const school = player.team?.name || player.teamName || '-';
  // Get finalScore from battingStats as playerScore - show "NA" if not available
  const playerScore = (() => {
    const stats = Array.isArray(player.battingStats) ? player.battingStats[0] : player.battingStats;
    const score = stats?.finalScore;
    if (score === null || score === undefined || score === '') return 'NA';
    return score;
  })();

  // Helper function to get stat value from player data
  const getStatValue = (key) => {
    // Define fallback mappings for different data formats
    const fallbackMapping = {
      // Batting stats
      'games_played': ['gp', 'games_played', 'gamesPlayed'],
      'at_bats': ['ab', 'at_bats', 'atBats'],
      'batting_average': ['avg', 'batting_average', 'battingAverage'],
      'on_base_percentage': ['ob_pct', 'on_base_percentage', 'onBasePercentage'],
      'slugging_percentage': ['slg_pct', 'slugging_percentage', 'sluggingPercentage'],
      'k_pct': ['k_pct', 'k_percentage', 'K_percent', 'strikeout_percentage'],
      'bb_pct': ['bb_pct', 'bb_percentage', 'BB_percent', 'walk_percentage'],
      'strikeout_percentage': ['k%', 'strikeout_percentage', 'strikeoutPercentage'],
      'walk_percentage': ['bb%', 'walk_percentage', 'walkPercentage'],
      'runs': ['r', 'runs'],
      'hits': ['h', 'hits'],
      'doubles': ['2b', 'doubles'],
      'triples': ['3b', 'triples'],
      'home_runs': ['hr', 'home_runs', 'homeRuns'],
      'rbi': ['rbi'],
      'total_bases': ['tb', 'total_bases', 'totalBases'],
      'walks': ['bb', 'walks'],
      'strikeouts': ['so', 'strikeouts'],
      'stolen_bases': ['sb', 'stolen_bases', 'stolenBases'],
      'caught_stealing': ['cs', 'caught_stealing', 'caughtStealing'],
      // Pitching stats
      'era': ['era', 'ERA'],
      'games_pitched': ['gp', 'app', 'games_pitched', 'gamesPitched', 'appearances'],
      'games_started': ['gs', 'games_started', 'gamesStarted'],
      'innings_pitched': ['ip', 'innings_pitched', 'inningsPitched'],
      'WHIP': ['whip', 'WHIP'],
      'wins': ['w', 'wins'],
      'losses': ['l', 'losses'],
      'saves': ['sv', 'saves'],
      'complete_games': ['cg', 'complete_games', 'completeGames'],
      'shutouts': ['sho', 'shutouts'],
      'hits_allowed': ['h', 'hits_allowed', 'hitsAllowed', 'hits'],
      'runs_allowed': ['r', 'runs_allowed', 'runsAllowed', 'runs'],
      'earned_runs': ['er', 'earned_runs', 'earnedRuns'],
      'walks_allowed': ['bb', 'walks_allowed', 'walksAllowed', 'walks'],
      'strikeouts_pitched': ['so', 'strikeouts_pitched', 'strikeoutsPitched', 'strikeouts'],
      'home_runs_allowed': ['hr', 'home_runs_allowed', 'homeRunsAllowed', 'homeRuns'],
      // Fielding stats
      'putouts': ['po', 'putouts'],
      'assists': ['a', 'assists'],
      'errors': ['e', 'errors'],
      'fielding_percentage': ['fpct', 'fielding_percentage', 'fieldingPercentage'],
      'total_chances': ['tc', 'total_chances', 'totalChances'],
      'double_plays': ['dp', 'double_plays', 'doublePlays'],
      'passed_balls': ['pb', 'passed_balls', 'passedBalls'],
      'stolen_bases_allowed': ['sba', 'stolen_bases_allowed', 'stolenBasesAllowed', 'stolen_bases_against'],
      'runners_caught_stealing': ['rcs', 'runners_caught_stealing', 'runnersCaughtStealing'],
    };

    const possibleKeys = fallbackMapping[key] || [key];

    // Try direct property first
    for (const possibleKey of possibleKeys) {
      if (player[possibleKey] !== undefined && player[possibleKey] !== null) {
        return formatStatValue(player[possibleKey], key);
      }
    }

    // Try from battingStats
    if (player.battingStats && Array.isArray(player.battingStats) && player.battingStats.length > 0) {
      for (const possibleKey of possibleKeys) {
        const value = player.battingStats[0][possibleKey];
        if (value !== undefined && value !== null) {
          return formatStatValue(value, key);
        }
      }
    }

    // Try from pitchingStats
    if (player.pitchingStats && Array.isArray(player.pitchingStats) && player.pitchingStats.length > 0) {
      for (const possibleKey of possibleKeys) {
        const value = player.pitchingStats[0][possibleKey];
        if (value !== undefined && value !== null) {
          return formatStatValue(value, key);
        }
      }
    }

    // Try from fieldingStats
    if (player.fieldingStats && Array.isArray(player.fieldingStats) && player.fieldingStats.length > 0) {
      for (const possibleKey of possibleKeys) {
        const value = player.fieldingStats[0][possibleKey];
        if (value !== undefined && value !== null) {
          return formatStatValue(value, key);
        }
      }
    }

    return '0';
  };

  // Format stat value
  const formatStatValue = (value, columnKey) => {
    if (value === null || value === undefined || value === '') return '0';

    // Handle MongoDB Decimal128 objects
    if (typeof value === 'object' && value.$numberDecimal) {
      value = parseFloat(value.$numberDecimal);
    }

    if (typeof value === 'number') {
      if (value === 0) return '0';
      if (columnKey === 'era' || columnKey === 'ERA' || columnKey === 'xfip' || columnKey === 'xFIP' || columnKey === 'WHIP' || columnKey === 'whip') return value.toFixed(2);
      if (columnKey === 'ops' || columnKey === 'on_base_plus_slugging') return value.toFixed(3);
      if (columnKey === 'innings_pitched' || columnKey === 'ip' || columnKey === 'inningsPitched') return value.toFixed(1);
      if (value < 1 && value > 0) {
        return value.toFixed(3);
      }
      return value % 1 === 0 ? value : value.toFixed(3);
    }
    // Handle string values (like ".391" or "50")
    if (typeof value === 'string') {
      // If it's already formatted with a decimal, return as is
      if (value.includes('.')) return value;
      // Otherwise return the string value
      return value;
    }
    return value;
  };

  return (
    <tr onClick={openOnRowClick(handleViewProfile)} className="cursor-pointer hover:bg-slate-800/40 transition border-b border-slate-700">
      {/* JP RANK */}
      {/* <td className="px-2 sm:px-3 py-2 sm:py-3 text-white font-semibold text-[10px] sm:text-xs whitespace-nowrap">
        {jpRank}
      </td> */}

      {/* NAME */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-white text-[10px] sm:text-xs whitespace-nowrap text-center">
        {playerName}
      </td>

      {/* POSITION */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-300 text-[10px] sm:text-xs whitespace-nowrap text-center">
        {position}
      </td>

      {/* CLASS */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-300 text-[10px] sm:text-xs whitespace-nowrap text-center">
        {playerClass}
      </td>

      {/* SCHOOL */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-400 text-[10px] sm:text-xs whitespace-nowrap text-center">
        {school}
      </td>

      {/* Dynamic stat columns */}
      {dynamicColumns.map((col, idx) => (
        <td
          key={`stat-${col.key}-${idx}`}
          className="px-2 sm:px-3 py-2 sm:py-3 text-cyan-200 font-semibold text-[10px] sm:text-xs whitespace-nowrap"
        >
          {getStatValueFromParent ? getStatValueFromParent(player, col) : getStatValue(col.key)}
        </td>
      ))}

      {/* PLAYER SCORE (finalScore from battingStats) */}
      {/* <td className="px-2 sm:px-3 py-2 sm:py-3 text-cyan-200 font-semibold text-[10px] sm:text-xs whitespace-nowrap">
        {playerScore}
      </td> */}

      {/* ACTIONS */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 sticky right-0 bg-slate-900/95 z-10">
        <div className="flex flex-row gap-1 sm:gap-2 min-w-[160px] sm:min-w-[180px]">
          <button
            onClick={handleViewProfile}
            className="border border-gray-400 bg-[#2e3337] text-white py-1 px-2 sm:px-3 rounded-md text-[10px] sm:text-xs leading-tight hover:bg-slate-800 transition whitespace-nowrap"
          >
            View profile
          </button>
          <button
            onClick={handleFollowToggle}
            className={`${
              player.isFollowing
                ? 'bg-gray-600 hover:bg-gray-700'
                : 'bg-cyan-500 hover:bg-cyan-400'
            } text-white font-semibold py-1 px-2 sm:px-3 rounded-md text-[10px] sm:text-xs leading-tight transition whitespace-nowrap`}
          >
            {player.isFollowing ? 'Unfollow' : 'Follow'}
          </button>
        </div>
      </td>
    </tr>
  );
};
